import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CACHE_KEY = 'siteData_v1';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const defaultData = {
    welcome: null,
    upcomingEvents: [],
    pastEvents: [],
    studies: [],
    // Site settings (page banners etc.). settingsLoaded lets page heroes hold a
    // neutral background instead of flashing the static default while we fetch.
    settings: {},
    settingsLoaded: false,
    loading: true,
};

function readCache() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (Date.now() - cached._ts > CACHE_TTL) {
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        }
        return cached;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, _ts: Date.now() }));
    } catch {
        // storage full — ignore
    }
}

const SiteDataContext = createContext(defaultData);

export function SiteDataProvider({ children }) {
    const cached = readCache();
    const [data, setData] = useState(cached
        ? {
            welcome: cached.welcome,
            upcomingEvents: cached.upcomingEvents || [],
            pastEvents: cached.pastEvents || [],
            studies: cached.studies,
            settings: cached.settings || {},
            settingsLoaded: !!cached.settings,
            loading: false,
        }
        : defaultData
    );

    const fetchAll = useCallback(async () => {
        try {
            const [welcomeRes, upcomingRes, pastRes, studiesRes, upcomingServicesRes, settingsRes] = await Promise.all([
                fetch(`${API_URL}/welcome`).then(r => r.json()).catch(() => null),
                fetch(`${API_URL}/events?timeframe=upcoming&limit=6&featured_home=true`).then(r => r.json()).catch(() => null),
                fetch(`${API_URL}/events?timeframe=past&limit=3`).then(r => r.json()).catch(() => null),
                fetch(`${API_URL}/digging-deep?limit=6&status=published`).then(r => r.json()).catch(() => null),
                fetch(`${API_URL}/service-times/upcoming?days=30`).then(r => r.json()).catch(() => null),
                fetch(`${API_URL}/settings`).then(r => r.json()).catch(() => null),
            ]);

            const recurringInstances = (upcomingServicesRes && upcomingServicesRes.success && upcomingServicesRes.upcoming) || [];
            const oneOffEvents = (upcomingRes && upcomingRes.success && upcomingRes.events) || [];

            // Tag and merge: services first by date, then events.
            const tagged = [
                ...recurringInstances.map(inst => ({
                    id: `svc-${inst.service_id}-${inst.event_date}`,
                    title: inst.title,
                    description: inst.description,
                    location: inst.location,
                    icon: inst.icon,
                    thumbnail: inst.thumbnail,
                    event_date: inst.event_date,
                    is_recurring: true,
                    recurrence: inst.recurrence,
                })),
                ...oneOffEvents.map(ev => ({ ...ev, is_recurring: false })),
            ].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

            const fresh = {
                welcome: (welcomeRes && welcomeRes.success && welcomeRes.welcome) || null,
                upcomingEvents: tagged.slice(0, 6),
                pastEvents: (pastRes && pastRes.success && pastRes.events) || [],
                studies: (studiesRes && studiesRes.success && studiesRes.studies) || [],
                settings: (settingsRes && settingsRes.success && settingsRes.settings) || {},
                settingsLoaded: true,
                loading: false,
            };

            setData(fresh);
            writeCache(fresh);
        } catch {
            setData(prev => ({ ...prev, loading: false, settingsLoaded: true }));
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    return (
        <SiteDataContext.Provider value={data}>
            {children}
        </SiteDataContext.Provider>
    );
}

export const useSiteData = () => useContext(SiteDataContext);
export default SiteDataContext;
